import React, { useMemo, useState } from 'react';
import withSizes from 'react-sizes';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import i18next from "i18next";
import { EntityIcon, EmbeddedEntityDetail } from "../Entity";
import ScrollArea from 'react-scrollbar';
import { ScrollModule } from '../ScrollModule';
import ReactPanZoom from "@ajainarayanan/react-pan-zoom";
import { ContentService } from '../../services';

import config from "config";

const EntityGroupingAMTDetail = ({ grouping, lastUpdated, connectionLines, areasToEntities, areaFilterIncludeFilter, statusIncludeFilter, entities, onEntityClick, isMobile }) => {
  const [loading, setLoading] = useState(true);
  const [mapURL, setMapURL] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [dx, setDX] = useState(areasToEntities ? -120 : 0);
  const [dy, setDY] = useState(0);

  useMemo(() => {
    if (grouping.amtGroupingSupportEntries) {
      setLoading(true);

      const floorPlanAMTData = grouping.amtGroupingSupportEntries.find(supportEntry => supportEntry.groupAssociableMetadataType.token === 'floor_plan_location');

      if (floorPlanAMTData) {
        ContentService.getDeviceGroupingImage(grouping.id, floorPlanAMTData.value).then((response) => {
          setMapURL(response);
          setLoading(false);
        }).catch((error) => {
          console.error(error);
          setMapURL(null);
          setLoading(false);
        });
      } else {
        setMapURL(null);
        setLoading(false);
      }
    } else {
      setMapURL(null);
      setLoading(false);
    }
  }, [grouping]);

  const zoomIn = () => {
    setZoom(zoom + 0.2);
  }

  const zoomOut = () => {
    if (zoom > 0.4) {
      setZoom(zoom - 0.2);
    }
  }

  const onPan = (dx, dy) => {
    setDX(dx);
    setDY(dy);
  }

  if (loading) { 
    return (<div className="loader">{ i18next.t('loading') }</div>)
  } else if (mapURL == null) {
    return <div>{ i18next.t('pages.maps.no-floor-plan') }</div>;
  } else if (mapURL === 'images/icon/no_image_icon.svg') {
    return <div>{ i18next.t('pages.maps.no-floor-plan-image') }</div>
  } else {    
    const storeMapImageWrapper = `store-map-image-wrapper ${lastUpdated ? "store-map-last-updated" : ""}`;
    const mapStyle = { position: "relative", backgroundImage: `url(${mapURL})`, width: 800, height: 600, backgroundSize: "800px 600px" };

    return (
      <div>
        <div className="container-fluid">
          <div className="col-sm-12">
            <ScrollModule/>
            <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal={true} >
              <ReactPanZoom zoom={zoom} pandx={dx} pandy={dy} onPan={onPan}>
                <div className={storeMapImageWrapper} style={mapStyle}>
                  { 
                    lastUpdated && <span style={{position: 'relative', top: '600px', left: '10px'}}>{ i18next.t('pages.maps.last-updated') } {lastUpdated}</span> 
                  }
                  
                  { connectionLines }
                  
                  <AMTOverlay 
                    zoom={zoom} 
                    areasToEntities={areasToEntities}
                    areaFilterIncludeFilter={areaFilterIncludeFilter}
                    statusIncludeFilter={statusIncludeFilter}
                    entities={entities}
                    onEntityClick={onEntityClick}
                    isMobile={isMobile} 
                    healthOnlyDisplay={connectionLines?.length > 0}
                  />

                  { areasToEntities && <AMTLegend areasToEntities={areasToEntities} /> }
                </div>
              </ReactPanZoom>
            </ScrollArea>

            <div className="op-floor-plan-zoom">
              <div className="row">
                <div className="col-sm-12">
                  <button onClick={zoomIn} type="button" className="btn btn-default btn-block btn-following-row"><span>+</span></button>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <button onClick={zoomOut} type="button" className="btn btn-default btn-block btn-following-row"><span style={{fontSize: '18px', fontWeight: '600', lineHeight:'22px'}}>-</span></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const findMapLocation = (entity) => {
  if (entity.deviceMetadata) {
    const xyMetadata = entity.deviceMetadata.filter((metadata) => metadata.deviceMetadataType.groupAssociableMetadataTypes.filter(groupAMT => groupAMT.token === 'floor_plan_location'));
    let xyCords = [-1, -1];
    xyMetadata.forEach(xyValue => {
      if (xyValue.deviceMetadataType.name === 'location_x') xyCords[0] = xyValue.numericValue;
      if (xyValue.deviceMetadataType.name === 'location_y') xyCords[1] = xyValue.numericValue;
    });
    return (xyCords[0] != null && xyCords[0] >= 0) && (xyCords[1] != null && xyCords[1] >= 0) ? [xyCords[0], xyCords[1]] : null;
  } else {
    var offsetX = 0;
    var offsetY = 0;
    if (entity.entityTypeToken === 'asset') {
      offsetX = 10;
      offsetY = -10;
    }
    return entity.floorPlanX != null && entity.floorPlanX >= 0 && entity.floorPlanY != null && entity.floorPlanY >= 0 ? [entity.floorPlanX + offsetX, entity.floorPlanY + offsetY] : null;
  }
};

const AMTOverlay = ({zoom, areasToEntities, areaFilterIncludeFilter, statusIncludeFilter, entities, onEntityClick, isMobile, healthOnlyDisplay}) => {

  const byAreaIncludes = (entity) => {
    if (areaFilterIncludeFilter) {
      return (entity.tags.filter(tag => tag.tagType.token === 'area').length === 0 && (areaFilterIncludeFilter.indexOf(-1) !== -1)) ||
              entity.tags.find(tag => areaFilterIncludeFilter.find(areaId => areaId === tag.id));
    } else {
      return true;
    }
  };

  const byStatus = (entity) => {
    if (statusIncludeFilter) {
        var foundEntity = entities?.find((ev) => ev.serialNumber === entity.serialNumber) ?? entity; // TODO not sure why this needs to be done

        const thresholdsExceeded = foundEntity.thresholdsExceeded ?? [];
        return thresholdsExceeded.find(threshold => statusIncludeFilter.find(thresholdId => thresholdId === threshold.id)) ||
                (statusIncludeFilter.includes(-1) && thresholdsExceeded.length === 0);
    } else {
      return true;
    }
  };

  if (areasToEntities) {
    var count = 1;
    return Object.entries(areasToEntities).map(([_, entities]) => (
      entities.map((entity, index) => {
        let xyCords = findMapLocation(entity);
        if (xyCords != null) {
          return (<div key={index} style = {{position: 'absolute', top: xyCords[1], left: xyCords[0], width: '24px', height: '24px'}} className="circle"><p>{count++}</p></div>);
        } else {
          count++;
        }
      })
    ));
  } else {
    return entities
              .filter(byAreaIncludes)
              .filter(byStatus)
              .map((entity) => {
                  const xyCords = findMapLocation(entity);

                  if (xyCords != null) {
                    const popoverHoverFocus = (
                      <Popover tabIndex="1" className="map-popover" id="popover-trigger-hover-focus" title={entity.displayName}>
                        <EmbeddedEntityDetail serialNumber={entity.serialNumber} healthOnlyDisplay={healthOnlyDisplay} />
                      </Popover>
                    );

                    const size = (24 / zoom).toString() + 'px';
                    const trigger = (isMobile === true ? "click" : ["hover", "focus"]);
                    const placement = (xyCords[1] > 400 ? 'top' : xyCords[0] > 300 ? 'left' : 'right');
                    const iconStyle = {position: 'absolute', top: xyCords[1], left: xyCords[0], width: size, height: size };

                    return (
                      <OverlayTrigger key={entity.serialNumber} trigger={trigger} overlay={popoverHoverFocus} shouldUpdatePosition={true} placement={placement} rootClose={isMobile}>
                        <EntityIcon style={iconStyle} entity={entity} onClick={() => onEntityClick ? onEntityClick(entity) : null} healthStatusOverride={healthOnlyDisplay} />
                      </OverlayTrigger>
                    );
                  } else {
                    return null;
                  }
              });
  }
};

const AMTLegend = ({areasToEntities}) => {
  var count = 1;
  var hasMissingCords = false;
  return (
    <div style = {{position:'relative', left: '800px'}}>
    {
      Object.entries(areasToEntities).map(([area, entities]) => (
        <div key={ area }>
          <b>{area}</b>
          {
            entities.map((entity, index) => {
                const xyCords = findMapLocation(entity);
                if (xyCords == null) hasMissingCords = true;
                return (
                  <div key={index} style = {{marginLeft: '30px'}}>
                    {count++}. {entity.displayName || entity.name} {xyCords == null ? <span>*</span> : null}
                  </div>
                );
              }
            )
          }
        </div>
      ))
    }
    { hasMissingCords && <div style={{paddingTop: 10}}>* Missing map location</div>}
    </div>
  );
}

export default withSizes(({ width }) => ({ isMobile: width < 830 }))(EntityGroupingAMTDetail);
